// Components/Pages/Admin/AdminLayout.jsx
import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link, Outlet } from "react-router-dom";
import { 
  FaBook, 
  FaProjectDiagram, 
  FaHeart, 
  FaImages, 
  FaFilePdf, 
  FaVideo, 
  FaSignOutAlt,
  FaBars,
  FaTimes,
  FaHome,
  FaArrowLeft
} from "react-icons/fa";

const AdminLayout = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await fetch('https://api.rigokenya.org/api/admin/me', {
        credentials: 'include'
      });
      if (!response.ok) {
        navigate('/admin/login');
        return;
      }
      const data = await response.json();
      setAdmin(data);
    } catch (error) {
      navigate('/admin/login');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const handleLogout = async () => {
    try {
      await fetch('https://api.rigokenya.org/api/admin/logout', {
        method: 'POST',
        credentials: 'include'
      });
      localStorage.removeItem('adminUser');
      navigate('/admin/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  // Navigation menu items
  const menuItems = [
    { icon: FaHome, label: 'Dashboard', path: '/admin/dashboard' },
    { icon: FaBook, label: 'Thematic Pillars', path: '/admin/pillars' },
    { icon: FaProjectDiagram, label: 'Projects', path: '/admin/projects' },
    { icon: FaHeart, label: 'Stories', path: '/admin/stories' },
    { icon: FaImages, label: 'Gallery', path: '/admin/gallery' },
    { icon: FaFilePdf, label: 'Resources', path: '/admin/resources' },
    { icon: FaVideo, label: 'Media', path: '/admin/media' },
    { icon: FaVideo, label: 'Blog', path: '/admin/blog' },
  ];

  // Get current page title from path
  const getPageTitle = () => {
    const path = window.location.pathname;
    const item = menuItems.find(item => item.path === path);
    return item ? item.label : 'Dashboard';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primaryGold mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-primaryBlue text-white transition-all duration-300 flex flex-col fixed h-full z-50 shadow-xl`}>
        {/* Header */}
        <div className="p-4 border-b border-white/10">
          {sidebarOpen ? (
            <div className="flex flex-col">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span className="text-xl font-bold text-primaryGold">Rising</span>
                  <span className="text-xl font-bold text-white">to Greatness</span>
                </div>
                <button onClick={toggleSidebar} className="text-white/80 hover:text-white">
                  <FaTimes />
                </button>
              </div>
              <span className="text-xs text-gray-300 mt-1">Community Based Organization</span>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <span className="text-xl font-bold text-primaryGold">R</span>
              <button onClick={toggleSidebar} className="text-white/80 hover:text-white">
                <FaBars />
              </button>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4">
          <ul className="space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = window.location.pathname === item.path;
              return (
                <li key={item.path}>
                  <Link 
                    to={item.path} 
                    className={`flex items-center px-4 py-3 transition-colors ${!sidebarOpen ? 'justify-center' : ''} ${
                      isActive ? 'bg-white/20 border-l-4 border-primaryGold' : 'hover:bg-white/10'
                    }`}
                  >
                    <Icon className="text-lg" />
                    {sidebarOpen && <span className="ml-3">{item.label}</span>}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-white/10">
          <button 
            onClick={handleLogout}
            className={`w-full flex items-center px-4 py-2 hover:bg-white/10 transition-colors rounded-lg ${!sidebarOpen ? 'justify-center' : ''}`}
          >
            <FaSignOutAlt className="text-lg" />
            {sidebarOpen && <span className="ml-3">Logout</span>}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className={`flex-1 transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-20'}`}>
        {/* Top Bar */}
        <nav className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-40">
          <div className="px-6 py-3 flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <button onClick={toggleSidebar} className="text-gray-600 hover:text-primaryBlue transition-colors lg:hidden">
                {sidebarOpen ? <FaTimes /> : <FaBars />}
              </button>
              <h1 className="text-xl font-bold text-primaryBlue">
                {getPageTitle()}
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600 hidden md:block">
                Welcome, {admin?.username || 'Admin'}
              </span>
              <div className="w-8 h-8 bg-primaryGold rounded-full flex items-center justify-center text-primaryBlue font-bold">
                {admin?.username?.charAt(0).toUpperCase() || 'A'}
              </div>
              {/* Back to Website */}
              <Link 
                to="/" 
                className="text-sm text-primaryGold hover:text-yellow-600 flex items-center gap-1 ml-4"
              >
                <FaArrowLeft className="text-xs" />
                Back to Site
              </Link>
            </div>
          </div>
        </nav>

        {/* Page Content */}
        <div className="p-6">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;